/**
 * RerunDeltaCard — "What changed since v(N)" smart landing card.
 *
 * Rerun Reimagined RR1. Renders as the first thing the user sees when the
 * Rerun panel opens. Summarises the delta the panel computed via
 * `computeRerunDelta` so the user can confirm-and-Run without walking the
 * 4-tab wizard.
 *
 * Empty-delta state: copy + disabled-state for the parent's Run CTA. The
 * card itself does not own the Run button — RR2 wires the primary action
 * into the panel chrome so it can compose with the diff sidebar (RR5).
 *
 * Pure presentational component — all state lives in the panel.
 */
import React from 'react';
import { FileText, Database, Key, CheckCircle2, AlertTriangle, Info } from 'lucide-react';
import type { RerunDelta } from '../../../lib/rerunDelta';
import { isRerunDeltaEmpty, providerLabel } from '../../../lib/rerunDelta';

export interface RerunDeltaCardProps {
  delta: RerunDelta;
  /** Pinned SED version on the report (for the "v(N) → v(M)" label). */
  pinnedSedVersion?: number;
  /** Live SED version on the site (for the "v(N) → v(M)" label). */
  liveSedVersion?: number;
  /** Click handler for the "View provider details" / "Resolve gaps" CTA. */
  onResolveGaps?: () => void;
  /** Click handler for "Open Configuration to edit before rerunning". */
  onOpenConfig?: () => void;
  /**
   * SCR-3 (Site Configuration Stickiness on Rerun, row 0.99291) — when
   * `true`, render the "Pulled from live site" banner above the delta
   * rows. The parent FeasibilityRerunPanel sets this flag when the
   * SCR-1 resolver landed on the `'live-site'` branch for ANY of the
   * three site-config surfaces (projectIntent / siteContext /
   * typedPreferences). The banner explains the source of the pulled
   * values + exposes the `[↩ Use original]` revert button.
   */
  scrShowPulledFromSiteBanner?: boolean;
  /** ISO 8601 timestamp of the report's createdAt — anchors the
   *  "Edits made after {date}" sentence in the banner copy. */
  scrReportGeneratedAt?: string;
  /** ISO 8601 timestamp of `site.siteConfigUpdatedAt` — drives the
   *  "as of {date}" sentence on the right-hand chip of the banner. */
  scrSiteConfigUpdatedAt?: string;
  /** Click handler for the SCR-3 `[↩ Use original]` revert button.
   *  Clears the dirty flag in the parent + writes the snapshot value
   *  back into local state. Optional — when absent, the button is hidden. */
  onScrRestoreOriginal?: () => void;
}

function pluralS(n: number): string {
  return n === 1 ? '' : 's';
}

function DeltaRow({
  icon,
  label,
  detail,
  tone = 'neutral',
}: {
  icon: React.ReactNode;
  label: string;
  detail?: string;
  tone?: 'neutral' | 'success' | 'warning';
}) {
  const toneClass =
    tone === 'success'
      ? 'text-success'
      : tone === 'warning'
        ? 'text-warning'
        : 'text-text-primary';
  return (
    <div className="flex items-start gap-2 text-[10px]" data-testid="rerun-delta-row">
      <span className={`shrink-0 mt-0.5 ${toneClass}`} aria-hidden="true">{icon}</span>
      <div className="flex-1 min-w-0">
        <div className={`font-medium ${toneClass}`}>{label}</div>
        {detail && (
          <div className="type-body text-text-muted truncate">{detail}</div>
        )}
      </div>
    </div>
  );
}

/**
 * SCR-3 banner copy — kept verbatim with the SCR pack's `Site_Config_
 * Rerun_Stickiness_Prompt.md` Implementation block. `toLocaleString` is
 * used (not `toLocaleDateString`) so the "Edits made after …" sentence
 * shows hour+minute precision; same-day edits read meaningfully instead
 * of collapsing to a date-only string.
 */
function formatScrBannerDate(iso: string | undefined): string {
  if (!iso) return '';
  const ms = new Date(iso).getTime();
  if (!Number.isFinite(ms)) return '';
  return new Date(ms).toLocaleString();
}

export function RerunDeltaCard({
  delta,
  pinnedSedVersion,
  liveSedVersion,
  onResolveGaps,
  onOpenConfig,
  scrShowPulledFromSiteBanner = false,
  scrReportGeneratedAt,
  scrSiteConfigUpdatedAt,
  onScrRestoreOriginal,
}: RerunDeltaCardProps) {
  const empty = isRerunDeltaEmpty(delta);

  // Distinct branch for the "unresolved gaps" path — when the SED still
  // carries unresolved gaps, that's the highest-priority thing to surface
  // even if other delta rows would also fire.
  const hasUnresolvedGaps = delta.pendingGaps > 0;

  return (
    <div
      className="border border-border bg-surface-1 p-3 space-y-2"
      data-testid="rerun-delta-card"
    >
      <div className="flex items-center justify-between">
        <div className="type-body font-bold uppercase tracking-widest text-text-secondary">
          What changed
        </div>
        {empty && !hasUnresolvedGaps && (
          <span
            className="type-body uppercase tracking-widest text-text-muted"
            data-testid="rerun-delta-empty-label"
          >
            No change detected
          </span>
        )}
      </div>

      {/* SCR-3 (Site Configuration Stickiness on Rerun, row 0.99291) —
          "Pulled from live site" banner. Fires when the parent's
          freshest-wins resolver landed on the `'live-site'` branch
          for ANY of projectIntent / siteContext / typedPreferences.
          The `[↩ Use original]` button reverts to the report's snapshot
          and clears the dirty flag so SCR-2's mid-flight resync can
          resume tracking subsequent live-site updates. */}
      {scrShowPulledFromSiteBanner && (
        <div
          data-testid="scr-pulled-from-site-banner"
          className="rounded-lg border border-primary/30 bg-primary/5 px-3 py-2 text-[10px] text-text-primary flex items-start gap-2"
        >
          <Info className="w-3 h-3 text-primary shrink-0 mt-0.5" aria-hidden="true" />
          <div className="flex-1 min-w-0">
            <div className="font-bold uppercase tracking-widest type-body mb-0.5">
              Site Project Intent has been updated since this report was generated
            </div>
            <div className="text-text-secondary type-body">
              Pulling live values from <code className="font-mono">site.projectIntent</code>.
              {scrReportGeneratedAt && (
                <> Edits made on the Site Overview tab after{' '}
                <span data-testid="scr-banner-report-generated-at">
                  {formatScrBannerDate(scrReportGeneratedAt)}
                </span>{' '}
                are applied.</>
              )}
              {scrSiteConfigUpdatedAt && (
                <>
                  {' '}
                  <span className="text-text-muted">
                    (Site config last updated{' '}
                    <span data-testid="scr-banner-site-updated-at">
                      {formatScrBannerDate(scrSiteConfigUpdatedAt)}
                    </span>
                    .)
                  </span>
                </>
              )}
            </div>
          </div>
          {onScrRestoreOriginal && (
            <button
              type="button"
              onClick={onScrRestoreOriginal}
              data-testid="scr-restore-original-button"
              className="text-[10px] underline text-primary hover:text-primary/80 shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              ↩ Use original
            </button>
          )}
        </div>
      )}

      <div className="space-y-1.5">
        {empty && !hasUnresolvedGaps && (
          <DeltaRow
            icon={<Info className="w-3.5 h-3.5" />}
            label="Nothing has changed since the last run"
            detail="A rerun would reproduce the same report. Open Configuration to edit inputs before rerunning."
            tone="neutral"
          />
        )}

        {hasUnresolvedGaps && (
          <DeltaRow
            icon={<AlertTriangle className="w-3.5 h-3.5" />}
            label={`${delta.pendingGaps} unresolved gap${pluralS(delta.pendingGaps)}`}
            detail="Resolve gaps below before rerunning for a stronger report."
            tone="warning"
          />
        )}

        {delta.newDocuments.length > 0 && (
          <DeltaRow
            icon={<FileText className="w-3.5 h-3.5" />}
            label={`${delta.newDocuments.length} new document${pluralS(delta.newDocuments.length)}`}
            detail={delta.newDocuments.slice(0, 2).map(d => d.fileName).join(', ')}
            tone="success"
          />
        )}

        {delta.sedVersionDrift != null && delta.sedVersionDrift !== 0 && (
          <DeltaRow
            icon={<Database className="w-3.5 h-3.5" />}
            label={
              pinnedSedVersion != null && liveSedVersion != null
                ? `SED upgraded v${pinnedSedVersion} → v${liveSedVersion}`
                : `SED ${delta.sedVersionDrift > 0 ? 'newer' : 'older'} by ${Math.abs(delta.sedVersionDrift)} version${pluralS(Math.abs(delta.sedVersionDrift))}`
            }
            detail={
              delta.upgradedSections.length > 0
                ? `${delta.upgradedSections.length} section${pluralS(delta.upgradedSections.length)} upgraded`
                : undefined
            }
            tone="success"
          />
        )}

        {delta.providerKeysAdded.length > 0 && (
          <DeltaRow
            icon={<Key className="w-3.5 h-3.5" />}
            label={`${delta.providerKeysAdded.length} provider${pluralS(delta.providerKeysAdded.length)} now configured`}
            detail={delta.providerKeysAdded.map(p => providerLabel(p)).join(', ')}
            tone="success"
          />
        )}

        {delta.resolvedGaps > 0 && (
          <DeltaRow
            icon={<CheckCircle2 className="w-3.5 h-3.5" />}
            label={`${delta.resolvedGaps} gap${pluralS(delta.resolvedGaps)} resolved since last run`}
            tone="success"
          />
        )}

        {delta.configChanged && (
          <DeltaRow
            icon={<Info className="w-3.5 h-3.5" />}
            label="Configuration edited"
            detail={
              // RDF-1 — when granular signals are available, surface a
              // more specific detail string than the generic copy.
              [
                delta.projectIntentChanged ? 'Project intent' : null,
                delta.typedPrefsChanged ? 'Report preferences' : null,
                delta.siteContextChanged ? 'Site context' : null,
              ].filter(Boolean).join(', ')
              || 'Project intent or report-type settings differ from the saved snapshot.'
            }
            tone="neutral"
          />
        )}

        {/* RDF-4 — Apply-on-Rerun queue. Surfaces sanityFindings the user
            marked 'apply' so the landing card reflects pending work that
            the rerun will incorporate. */}
        {(delta.appliedFindingsCount ?? 0) > 0 && (
          <DeltaRow
            icon={<CheckCircle2 className="w-3.5 h-3.5" />}
            label={`${delta.appliedFindingsCount} insight${pluralS(delta.appliedFindingsCount ?? 0)} marked 'Apply on rerun'`}
            detail={
              delta.appliedFindingsPreview && delta.appliedFindingsPreview.length > 0
                ? (
                  delta.appliedFindingsPreview.join(' · ')
                  + (
                    (delta.appliedFindingsCount ?? 0) > delta.appliedFindingsPreview.length
                      ? ` · +${(delta.appliedFindingsCount ?? 0) - delta.appliedFindingsPreview.length} more`
                      : ''
                  )
                )
                : undefined
            }
            tone="success"
          />
        )}
      </div>

      {/* SOC — snapshot provenance caption. Surfaces whether the report's
          *AtGen snapshots came from the SED (the SED was internally
          consistent with the user's overview at compile time) or the live
          store (legacy / pre-SOC). Pre-SOC reports leave the field
          undefined; we default to 'live store' for that branch. */}
      <div
        className="type-body text-text-muted pt-1 border-t border-border/30"
        data-testid="rerun-delta-snapshot-provenance"
      >
        Snapshots:{' '}
        {delta.siteOverviewSnapshotProvenance === 'sed'
          ? 'SED compile'
          : 'live store'}
      </div>

      {(hasUnresolvedGaps || empty) && (onResolveGaps || onOpenConfig) && (
        <div className="flex justify-end gap-2 pt-1">
          {hasUnresolvedGaps && onResolveGaps && (
            <button
              type="button"
              onClick={onResolveGaps}
              className="type-body font-bold uppercase tracking-widest text-warning hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-warning"
              data-testid="rerun-delta-resolve-gaps"
            >
              Resolve gaps →
            </button>
          )}
          {empty && !hasUnresolvedGaps && onOpenConfig && (
            <button
              type="button"
              onClick={onOpenConfig}
              className="type-body font-bold uppercase tracking-widest text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              data-testid="rerun-delta-open-config"
            >
              Open Configuration →
            </button>
          )}
        </div>
      )}
    </div>
  );
}
